import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import RESOURCES from '~/resources';
// import Button from '~/components/Button';
const Button = dynamic(() => import('~/components/Button'), { ssr: false });

import TooltipInfo from '~/components/TooltipInfo';
import CarTopLabel from '~/components/CarTopLabel';
import CarImagesCarousel from '~/components/CarImagesKitCarousel';
import { useUsedCarPageUrl } from './useUsedCarPageUrl';
import { CAR_FLOW_TYPE } from '~/data/car/carCardData';
import { BUTTON_SIZES, BUTTON_TYPES } from '~/data/constants';
import { formatPrice } from '~/hooks/useFormatPrice';
import { useInitialCarDealOption } from '~/hooks/useInitialCarDealOption';
import { EActionEvent, ECategoryEvent, useEvents } from '~/hooks/useEvents';
import { UsedCarCardProps } from '~/data/types/usedCar/usedCarData';
import { COLORS } from '~/data/ui/colors';
import { CAR_TARGET, CONTACT_US_REDIRECTION } from '~/data/consts';
import { imageKitThumb } from '~/data/imageKitData';
import { ESourceEventOptions } from '~/data/lead/enums';
import { subMedia } from '~/data/lead/subMedia';
import { EContactUsFlow } from '~/data/ContactUsPopup/enums';
import { CampaignNumber } from '~/services/LeadService/enums';
import { sortLabelsByRank } from '~/utils/cleanObject';
import { setCarType } from '~/redux-toolkit/slices/carSlice';
import useStyles, {
  CarImagesCarouselStyle,
  CarNextUrlStyle,
  brandNameStyle,
  buttonStyle,
  contactUsButton,
  contactUsButtonText,
  modelNameStyle,
  priceStyle,
  purchasingDetailsStyle,
  labelStyles,
  yellowLineContainerStyles,
  yellowLineStyles,
  bottomPriceContainer,
  priceListCarCardStyle,
  tooltipCustomStyles,
  contentContainer,
  bottomContainer,
  StyledOnlinePriceImage,
  normalPriceStyle,
  exposedPriceStyle,
  priceWrapperStyles,
  exposePriceContainer,
  StyledOnlinePriceTag,
  customWishlistStyles,
  topLabelStyles,
  customCompareButton,
  DiscountLabel,
} from './useStyles';
import { ROUTES } from '~/data/routes';
import useStringTemplate from '~/hooks/useStringTemplate';
import { useAppSelector } from '~/redux-toolkit/store/hooks';
import { RootState } from '~/redux-toolkit/store/store';
import { useContactUsPopup } from '~/hooks/useContactUsPopup';
import { useLeadEventDetails } from '~/hooks/useLeadEventDetails';
import YellowLineTitle from '~/assets/icons/jsx/yellowLineTitle';
import OnlinePriceImage from '~/assets/images/jsx/OnlinePriceImage';
import WishlistButton from '../WishlistButton';
import OnlinePriceWrapper from '~/assets/images/jsx/OnlinePriceWrapper';
import OnlinePriceTag from '~/assets/images/jsx/OnlinePriceTag';
import { ARIA_LABELS_RESOURCES } from '~/resources/resources';
import { generateSeed, getRandomIndices } from '~/utils/getRandomIndices';
import { useCarDetailsNote } from '~/hooks/useCarDetailsNote';
import { getCookie } from '~/utils/cookieStorageHandlers';
const CompareButton = dynamic(
  () => import('~/components/ComparePopover/CompareButton'),
  {
    ssr: false,
  },
);
import useCompareCars from '../ComparePopover/useCompareCars';
import { CompareCarTypeEnum } from '~/data/types/compare/compareTypes';
import { animateImageToDiv } from '~/utils/AnimateImageToDiv';
import GlowWrapper from '../GlowingWrapper';
import Link from 'next/link';
import { hrefBtnStyle } from '../CarCard/useStyles';
const FomoLabel = dynamic(() => import('../FomoLabel/FomoLabel'), {
  ssr: false,
});
export const UsedCarCard: FC<UsedCarCardProps> = ({
  car,
  isInWishlist,
  onWishlistButtonClicked,
  isV2 = false,
}) => {
  const events = useEvents();
  const classes = useStyles();
  const dispatch = useDispatch();
  const formatString = useStringTemplate();
  const { getCarDetailsNote } = useCarDetailsNote();
  const { openContactUsModal } = useContactUsPopup();
  const { usedCarComparison, toggleCarInCompare } = useCompareCars();
  const carFlowDefaultValue = useInitialCarDealOption(car, ROUTES.USED_CAR);

  const { usedCarTooltipDesc } = useAppSelector(
    (state: RootState) => state.funding,
  );

  const [selectedButton, setSelectedButton] = useState(carFlowDefaultValue);
  const [isPriceExposed, setIsPriceExposed] = useState(false);

  const [onlinePriceColor, setOnlinePriceColor] = useState(
    COLORS.primary.light2,
  );
  const [isCompared, setIsCompared] = useState(false);
  const [showCompareWrapper, setShowCompareWrapper] = useState(false);

  useEffect(() => {
    const hasVisitedUsedCarListingsPage = getCookie(
      'hasVisitedUsedCarListingsPage',
    );
    setShowCompareWrapper(hasVisitedUsedCarListingsPage === null);
  }, []);

  // Fomo Label
  const shouldShowFomo = useMemo(() => {
    const viewThreshold = 20;
    return car.pageViewsThreeDaysAgo > viewThreshold;
  }, [car.pageViewsThreeDaysAgo]);

  // Car Images
  const images = useMemo(() => {
    if (Array.isArray(car.imagesKit) && car.imagesKit?.length > 0) {
      return car.imagesKit.map((img) => img.path || img.url);
    }
    if (Array.isArray(car.images) && car.images?.length > 0) {
      return car.images.map((img) => img.url);
    }

    return [];
  }, [car?.images, car?.imagesKit]);

  const ikThumb: string = useMemo(() => {
    return imageKitThumb.carCard;
  }, []);

  // Wish list click logic
  const onWishlistIconClicked = useCallback(() => {
    const action = isInWishlist
      ? EActionEvent.REMOVE_WISHLIST
      : EActionEvent.ADD_TO_WISHLIST;
    events.sendEvent(
      {
        action,
        category: ECategoryEvent.USER,
      },
      {
        carMake: car.carZoneManufacturerName,
        carModel: car.carZoneModelName,
        carId: car.id,
        businessLine: CAR_TARGET.USED_CAR,
      },
    );
    onWishlistButtonClicked(Number(car.id));
  }, [car, events, isInWishlist, onWishlistButtonClicked]);

  // Get Gir
  const getGir = useCallback((gir: number) => {
    return gir === 1
      ? RESOURCES.GEAR_AUTOMATIC
      : gir === 0 && RESOURCES.GEAR_MANUALLY;
  }, []);

  // Top Label logic
  const topLabel = useMemo(() => {
    if (car.carLabels && car.carLabels.length > 0) {
      const sortedLabels = sortLabelsByRank(
        car.carLabels.filter(
          (label) => !label.isPrioritized && label.type !== 'promotion',
        ),
      );
      return sortedLabels[0]?.title ?? '';
    }

    if (car.carLabels && car.carLabels.length > 0) {
      const seed = generateSeed(`${car.id}`, new Date());
      const randomIndices = getRandomIndices(car.carLabels.length, 1, seed);
      return car.carLabels[randomIndices[0]]?.title ?? '';
    }

    return '';

    // TBD: Prioritized Label
    const prioritizedLabel = car.carLabels.filter(
      (label: any) => label.isPrioritized,
    )[0];

    if (prioritizedLabel) {
      return prioritizedLabel.title.toLocaleLowerCase();
    }

    const firstLabel = car.carLabels[0];
    return firstLabel?.title || firstLabel?.attributes?.title || '';
  }, [car]);
  const topLabelElement = useMemo(() => {
    if (!topLabel) return null;

    // const prioritizedLabel = car.carLabels.filter(
    //   (label: any) => label.isPrioritized,
    // )[0];

    return <CarTopLabel title={topLabel} customTextStyle={topLabelStyles} />;
  }, [topLabel]);

  const onClickSubmitButton = useCallback(() => {
    events.sendEvent(
      {
        category: ECategoryEvent.RESULT_PAGE,
        action: EActionEvent.CAR_RESULTS_PAGE,
      },
      {
        carMake: car?.carZoneManufacturerName,
        carModel: car?.carZoneModelName,
        carYear: car.year,
        KM: car?.kms,
        hand: car?.carHand,
        dealType: selectedButton,
        businessLine: CAR_TARGET.USED_CAR,
        topLabel,
        gaLabel: shouldShowFomo,
      },
    );
    dispatch(setCarType(selectedButton));
  }, [selectedButton, dispatch, events, car, topLabel, shouldShowFomo]);

  // Purchasing Used car Details
  const purchasingUsedCarDetails = useMemo(() => {
    if (car) {
      return (
        <Typography sx={purchasingDetailsStyle}>
          <Box component="span" sx={labelStyles}>
            {RESOURCES.CAR_PURCHASING_DETAILS_KM.replace(
              'x',
              car.kms?.toLocaleString(),
            )}
          </Box>

          <Box component="span" sx={labelStyles}>
            {RESOURCES.CAR_PURCHASING_DETAILS_HANDS.replace(
              'x',
              car.carHand?.toLocaleString(),
            )}
          </Box>

          <Box component="span" sx={labelStyles}>
            {getGir(car.gir)}
          </Box>
        </Typography>
      );
    }
    return null;
  }, [car, getGir]);

  // Prime Rate
  const usedCarPrimeRate = car.smartPrice?.webSmartPriceLoanRate;

  // Legal Info per flow type / TODO: Simplify logic
  const legalInfo = useCallback(
    (flowType: CAR_FLOW_TYPE) => {
      switch (flowType) {
        case CAR_FLOW_TYPE.LOAN_CAR:
          return formatString(usedCarTooltipDesc, {
            usedCarPrimeRate,
          });
        default:
          return '';
      }
    },
    [formatString, usedCarPrimeRate, usedCarTooltipDesc],
  );

  // Next URL - Car page
  const carCardNextUrl = useUsedCarPageUrl(car, selectedButton);

  // Leave Details event collector
  const leadEventDetails = useLeadEventDetails(
    ESourceEventOptions.USED_CAR_RESULT_PAGE,
    car,
  );
  const onClickLeaveDetails = useCallback(() => {
    dispatch(setCarType(selectedButton));
    const { carProgrammaticDescription, ...rest } = leadEventDetails as any;
    events.sendEvent(
      {
        action: EActionEvent.CAR_PAGE_LEAD,
        category: ECategoryEvent.LEAD,
      },
      {
        carModel: car?.carZoneModelName,
        carMake: car?.carZoneManufacturerName,
        carYear: car?.year,
        ...rest,
        dealType: selectedButton,
        gaLabel: shouldShowFomo,
      },
    );

    openContactUsModal({
      source: ESourceEventOptions.USED_CAR_RESULT_PAGE,
      flow: EContactUsFlow.LEAVE_DETAILS,
      redirectTo: CONTACT_US_REDIRECTION.CAR_PAGE,
      campaignNumber: CampaignNumber.USED_CAR_RESULT_PAGE,
      subMedia: subMedia.contactUsCarCard,
      usedCar: car,
      note: getCarDetailsNote(CAR_TARGET.USED_CAR, {
        manufacturer: car.carZoneManufacturerName ?? '',
        model: car.modelNameEn ?? '',
        year: `${car.year}`,
        mileage: `${car.kms}`,
        carNumber: car.usedCarSpec?.carNumber,
      }),
      screen: 'usedCar',
      leadLocation: ESourceEventOptions.USED_CAR_RESULT_PAGE,
    });
  }, [
    getCarDetailsNote,
    dispatch,
    selectedButton,
    events,
    car,
    leadEventDetails,
    openContactUsModal,
    shouldShowFomo,
  ]);

  const carFullTitle = useMemo(() => {
    return `${car.carZoneManufacturerName} ${
      car.carZoneModelName || car.name
    } ${car.carZoneFinishedLevelName || ''}`;
  }, [car]);

  const leaveDetailsLabel = useMemo(() => {
    return RESOURCES.CAR_FLOW_SUMMARY_CONTACT_ME;
  }, []);

  // Compare Car
  const handleOnCompareClick = (isDisabled: boolean) => {
    if (isDisabled) return;
    const action = isCompared
      ? EActionEvent.REMOVE_COMPARE
      : EActionEvent.ADD_COMPARE;

    events.sendEvent(
      {
        action,
        category: ECategoryEvent.COMPARE,
      },
      {
        businessLine: CAR_TARGET.USED_CAR,
        location: ESourceEventOptions.USED_CAR_RESULT_PAGE,
        carMake: car.carZoneManufacturerName,
        carModel: car.carZoneModelName,
        carId: car.id,
        carYear: car?.year,
        kms: car.kms,
        carHand: car.carHand,
      },
    );

    // Trigger the image flying animation
    const imageToFly = document.querySelector(`.carousel-image-${car.id}`);
    const target = document.getElementById('compare-target');

    if (imageToFly && target && !isCompared) {
      animateImageToDiv(imageToFly, target);
    }
    toggleCarInCompare(car, CompareCarTypeEnum.USED);
  };

  const specialPrioritizedLabel = useMemo(() => {
    const prioritizedItem = car?.carLabels.find(
      (label) => label.isPrioritized || label.type === 'promotion',
    );

    if (
      !isPriceExposed &&
      prioritizedItem &&
      car?.carPrice &&
      car?.websitePrice &&
      car.carPrice > car.websitePrice &&
      prioritizedItem?.title.includes('הנחה')
    ) {
      const discount = ((car.carPrice - car.websitePrice) / car.carPrice) * 100;
      return `${Math.ceil(discount)}% ${RESOURCES.DISCOUNT}`;
    }

    if (!isPriceExposed && prioritizedItem) {
      return prioritizedItem.title;
    }

    return null;
  }, [car, isPriceExposed]);

  useEffect(() => {
    const onlinePrice = getCookie('onlinePriceExposed', true);
    if (onlinePrice?.exposedCarIds) {
      const uniqueCarIdsSet = new Set(onlinePrice.exposedCarIds);
      setIsPriceExposed(uniqueCarIdsSet.has(car.id));
    }
  }, [car.id]);

  useEffect(() => {
    if (carFlowDefaultValue !== selectedButton) {
      setSelectedButton(carFlowDefaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSelectedButton, carFlowDefaultValue]);

  useEffect(() => {
    setIsCompared(usedCarComparison.compareIds.includes(car.id));
  }, [car.id, usedCarComparison.compareIds]);

  return (
    <Box
      sx={classes.root}
      onMouseEnter={() => {
        setOnlinePriceColor(COLORS.primary.main);
      }}
      onMouseLeave={() => {
        setOnlinePriceColor(COLORS.primary.light2);
      }}
    >
      <Box sx={classes.cardContainer}>
        <Box sx={classes.sliderContainer}>
          <CarImagesCarousel
            ikThumb={ikThumb}
            images={images}
            customStyle={CarImagesCarouselStyle}
            carId={car.id}
          />

          {car?.carLabels?.length > 0 && (
            <Box sx={classes.bottomLabel}>{topLabelElement}</Box>
          )}
          <Box sx={classes.wishlistIcon}>
            <GlowWrapper show={showCompareWrapper}>
              <CompareButton
                onClick={(isDisabled) => handleOnCompareClick(isDisabled)}
                customButtonStyles={customCompareButton}
                chosen={isCompared}
                ariaLabel={`${RESOURCES.COMPARE_ADD_AND_COMPARE} ${carFullTitle}`}
              />
            </GlowWrapper>
            <WishlistButton
              onClick={onWishlistIconClicked}
              isClicked={isInWishlist}
              customStyles={customWishlistStyles}
              carTitle={carFullTitle}
            />
          </Box>
        </Box>
        <Box sx={bottomContainer}>
          {!isPriceExposed && !isV2 && (
            <OnlinePriceImage
              Component={StyledOnlinePriceImage}
              color={onlinePriceColor}
            />
          )}
          {/* CONTENT */}
          <CarNextUrlStyle href={carCardNextUrl} onClick={onClickSubmitButton}>
            <Box sx={contentContainer}>
              <Typography variant="h4" sx={brandNameStyle}>
                {`${car.carZoneManufacturerName || ''} | ${
                  car.year && car.year
                } `}
              </Typography>
              <Typography variant="h3" sx={modelNameStyle}>
                {`${car.carZoneModelName || car.name} ${
                  car.carZoneFinishedLevelName || ''
                }`}
              </Typography>
              {purchasingUsedCarDetails}
              {shouldShowFomo && (
                <FomoLabel
                  pageViews={car.pageViewsThreeDaysAgo}
                  label={RESOURCES.FOMO_USED_CAR_PAGE}
                  fomoContainerStyle={classes.fomoContainer}
                />
              )}
              {/* Bullets */}
              {/* {!carDiscount &&
              parsedCardBullets.map((cardBullet, i) => {
                const { resultsPageTitle } = cardBullet;
                return (
                  <Box sx={bulletContainer} key={`${resultsPageTitle}-${i}`}>
                    <BulletCard Component={StyledBulletIcon} />
                    <Typography sx={bulletTextStyles}>
                      {resultsPageTitle}
                    </Typography>
                  </Box>
                );
              })} */}
            </Box>
          </CarNextUrlStyle>
          {/* PRICE */}
          <Box>
            <Box
              sx={{
                ...classes.priceWrapper,
                ...(isPriceExposed ? classes.exposedPriceWrapper : null),
              }}
            >
              {!isPriceExposed && specialPrioritizedLabel && (
                <DiscountLabel>{specialPrioritizedLabel}</DiscountLabel>
              )}
              {isPriceExposed && (
                <Typography sx={exposePriceContainer}>
                  <Box
                    component="span"
                    sx={normalPriceStyle}
                    fontFamily="fontFamily"
                    fontWeight="800"
                  >
                    {formatPrice(
                      car?.websitePrice || car?.smartPrice?.webPrice,
                    )}
                  </Box>
                </Typography>
              )}

              <Typography
                variant="h3"
                sx={{
                  ...priceStyle,
                  ...(isPriceExposed ? exposedPriceStyle : null),
                }}
              >
                {isPriceExposed || isV2
                  ? formatPrice(car?.websiteSmartPrice)
                  : formatPrice(car?.websitePrice)}

                {isPriceExposed ? (
                  <Box component="span" sx={priceWrapperStyles}>
                    <OnlinePriceWrapper style={yellowLineStyles} />
                  </Box>
                ) : (
                  <Box component="span" sx={yellowLineContainerStyles}>
                    <YellowLineTitle style={yellowLineStyles} />
                  </Box>
                )}
              </Typography>

              {/* BOTTOM PRICE (LOAN)*/}
              <Box sx={bottomPriceContainer}>
                {!isPriceExposed && (
                  <Typography sx={priceListCarCardStyle}>
                    {car.smartPrice?.webSmartPriceLoanMinMonthlyPayment > 0 &&
                      `${RESOURCES.CAR_PURCHASE_OR} ${formatPrice(
                        car.smartPrice?.webSmartPriceLoanMinMonthlyPayment,
                      )} ${RESOURCES.CAR_PURCHASE_TIME_PERIOD}`}
                  </Typography>
                )}

                {isPriceExposed &&
                  car.smartPrice?.webPriceLoanMinMonthlyPayment > 0 && (
                    <Typography sx={priceListCarCardStyle}>
                      {`${RESOURCES.CAR_PURCHASE_OR} ${formatPrice(
                        car.smartPrice?.webPriceLoanMinMonthlyPayment,
                      )} ${RESOURCES.CAR_PURCHASE_TIME_PERIOD}`}
                    </Typography>
                  )}
                {car.smartPrice?.webPriceLoanMinMonthlyPayment > 0 && (
                  <TooltipInfo
                    buttonStyle={tooltipCustomStyles}
                    infoText={legalInfo(CAR_FLOW_TYPE.LOAN_CAR)}
                    ariaLabel={`${ARIA_LABELS_RESOURCES.CAR_CARD_TOOLTIP_MORE_INFO} ${RESOURCES.FUNDING}`}
                  />
                )}
              </Box>

              {isPriceExposed && (
                <OnlinePriceTag Component={StyledOnlinePriceTag} />
              )}
            </Box>
          </Box>
          {/* BUTTONS */}
          <Box sx={classes.btnsDivStyle}>
            <Button
              label={leaveDetailsLabel}
              size={BUTTON_SIZES.LARGE}
              type={BUTTON_TYPES.SECONDARY}
              customStyle={contactUsButton}
              typographyStyle={contactUsButtonText}
              onClickFunc={onClickLeaveDetails}
            />
            <Link
              href={carCardNextUrl}
              style={hrefBtnStyle}
              aria-label={`${RESOURCES.CAR_SUBMIT_BUTTON2} ${carFullTitle}`}
              className="car-next-flow"
            >
              <Button
                onClickFunc={onClickSubmitButton}
                label={RESOURCES.CAR_SUBMIT_BUTTON2}
                ariaLabel={`${RESOURCES.CAR_SUBMIT_BUTTON2} ${carFullTitle}`}
                customStyle={buttonStyle}
                type={BUTTON_TYPES.MAIN}
                size={BUTTON_SIZES.LARGE}
                roleVar="presentation"
                tabbable={false}
              />
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UsedCarCard;
